// The evaluations: enquêtes (à chaud, à froid) and questionnaires — their templates, their occurrences on a session,
// their respondents and the cross-session page.

// An evaluation template's type, valued as orders names it. DIRECT/DELAYED keep the app's chaud/froid vocabulary
// (SessionDetailsSatisfactionTypeEnum); the questionnaire is orders' « assessment ».
export enum EvaluationTypeEnum {
  DIRECT = "direct", // Enquête à chaud
  DELAYED = "delayed", // Enquête à froid
  QUESTIONNAIRE = "assessment", // Questionnaire noté (positionnement / validation des acquis)
}

// Builder chooser groups ("Sélectionnez un élément à ajouter") — a questionnaire offers all five, an enquête
// only the "libre" formats.
export enum EvaluationElementGroupEnum {
  FREE_QUESTION = "free_question", // Question libre
  SCORED_QUESTION = "scored_question", // Question notée
}

// `order_events.event_name` values the backend writes when it dispatches a satisfaction form — mirrors
// Orders' `SatisfactionFormEvent` (src/lib/enums/orders/satisfaction-forms.enum.ts). They arrive verbatim on
// SessionNotificationItem.eventName in the session's Notifications list. The native survey feature never emits the
// Typeform-era BookingEventsEnum.DIRECT_PEDAGOGICAL_REPORT / DELAYED_PEDAGOGICAL_REPORT, so anything keyed on those
// two names silently misses every real survey row. (QA #1216976309903335)
export enum SatisfactionFormEventEnum {
  HOT = "hot_satisfaction_form",
  COLD = "cold_satisfaction_form",
  RESEND = "satisfaction_form_resend",
}

// Which right-hand panel the builder shows.
export enum EvaluationBuilderPanelEnum {
  INFO = "info", // "Informations générales"
  ACTIVITY = "activity", // a selected activité (question or content)
}

// "Seuil de validation" bounds: a percentage of right answers on the scored questions.
export const QUESTIONNAIRE_THRESHOLD_MIN = 0;
export const QUESTIONNAIRE_THRESHOLD_MAX = 100;

// The 1-to-10 rating scale used by the "Notation 1 à 10" question type and the system question
// (PRD AC-QUEST-04.4 / AC-LEARN-02.5: fixed 1→10, no scale configuration).
export const SURVEY_RATING_MIN = 1;
export const SURVEY_RATING_MAX = 10;

// Survey-level status shown on a session's enquête.
export enum SessionSurveyStatusEnum {
  SCHEDULED = "scheduled", // Programmée
  SENT = "sent", // Envoyée
}

// Per-participant status inside a survey's table.
export enum SessionSurveyParticipantStatusEnum {
  SCHEDULED = "scheduled", // Programmé
  SENT = "sent", // Envoyé
  COMPLETED = "completed", // Complété (a répondu)
  PENDING = "pending", // En attente — a questionnaire is never sent, so it is only ever pending or completed
}

// Sortable columns of a participants table, an enquête's or a questionnaire step's.
export enum SurveyParticipantSortKeyEnum {
  PARTICIPANT = "attendeeName",
  STATUS = "status",
  SCORE = "score",
}

// "Nouvelle enquête" modal — send mode.
export enum SessionSurveySendModeEnum {
  NOW = "now", // Envoyer maintenant
  SCHEDULED = "scheduled", // Programmer l'envoi
}

// "Nouvelle enquête" modal — recipients.
export enum SessionSurveyRecipientEnum {
  PARTICIPANTS = "participants",
  MANAGERS = "managers",
}

// "Nouvelle enquête" modal — relative-offset scheduler (Programmer l'envoi).
// PRD AC-SESSEVAL-03.2: units are heure(s)/jour(s) only.
export enum SessionSurveyOffsetUnitEnum {
  HOUR = "hour", // heure(s)
  DAY = "day", // jour(s)
}

export enum SessionSurveyOffsetDirectionEnum {
  BEFORE = "before", // avant
  AFTER = "after", // après
}

export enum SessionSurveyOffsetAnchorEnum {
  SESSION_START = "session_start", // le début de la session
  SESSION_END = "session_end", // la fin de la session
}

// Which screen the evaluation player shows, an enquête's public link or a questionnaire's signed-in page.
export enum EvaluationPlayerStateEnum {
  LOADING = "loading",
  NOT_FOUND = "not_found", // unknown/invalid token
  ALREADY_SUBMITTED = "already_submitted", // participant already Complété — no re-edit (AC-LEARN-04.3)
  WIZARD = "wizard", // answering the questionnaire
  DONE = "done", // thank-you screen after submit
}

// « Type d'étape * » — the first screen of « Ajouter une étape » on a session.
export enum SessionStepTypeEnum {
  TRAINING = "training", // Classe, eLearning ou Mixte
  QUESTIONNAIRE = "questionnaire",
}

// Which « Ajouter une étape » encart is open under the session's steps.
export enum SessionStepAddStateEnum {
  CHOOSE_TYPE = "choose_type", // 1/2 — the type cards
  QUESTIONNAIRE = "questionnaire", // 2/2 — the questionnaire form
}

// Machine codes orders puts beside a questionnaire 400's message.
export enum QuestionnaireErrorCodeEnum {
  /** Completing with a required question unanswered: the body lists them in `missingQuestionIds`. */
  REQUIRED_QUESTIONS_MISSING = "REQUIRED_QUESTIONS_MISSING",
}

// Top-of-table sub-tabs on the cross-session Évaluations page.
export enum GlobalEvaluationsTabEnum {
  HOT = "hot", // Enquêtes à chaud → EvaluationTypeEnum.DIRECT
  COLD = "cold", // Enquêtes à froid → EvaluationTypeEnum.DELAYED
  QUESTIONNAIRES = "questionnaire", // Questionnaires → EvaluationTypeEnum.QUESTIONNAIRE
}

// The sort keys the questionnaire tab swaps: its score and its step's date sit in the note and send-date columns.
export enum EvaluationSortColumnEnum {
  GLOBAL_SCORE = "globalScore",
  SENT_AT = "sentAt",
  SCORE = "score",
  DATE = "date",
}

export enum EvaluationsExportFormatEnum {
  EXCEL = "excel",
  CSV = "csv",
}

const TAB_EVALUATION_TYPE: Record<GlobalEvaluationsTabEnum, EvaluationTypeEnum> = {
  [GlobalEvaluationsTabEnum.HOT]: EvaluationTypeEnum.DIRECT,
  [GlobalEvaluationsTabEnum.COLD]: EvaluationTypeEnum.DELAYED,
  [GlobalEvaluationsTabEnum.QUESTIONNAIRES]: EvaluationTypeEnum.QUESTIONNAIRE,
};

export const tabToEvaluationType = (tab: GlobalEvaluationsTabEnum): EvaluationTypeEnum => TAB_EVALUATION_TYPE[tab];
